import { COMPACT_BELOW_DESIGN_PX, type DeviceClass, type Orientation } from '@/config/uiScale';

/*
 * All widths and sizes here are design px (CSS px ÷ UI scale, see src/config/uiScale.ts). The
 * caller measures CSS px, divides by the scale, and renders the result × the scale.
 */

export type HomeGridLayout = {
  cols: number;
  slotGi: number;
  /** Inter-card gap (row and column). */
  gap: number;
  /** Horizontal edge inset — matched to gap (App Store / iOS springboard rhythm). */
  padX: number;
};

export type HomeGridBreakpointConfig = {
  minCols: number;
  maxCols: number;
  minGi: number;
  maxGi: number;
};

/**
 * Estimate content width inside `.lobby-home-bd` before mount
 * (shell max-width − provisional side pads).
 */
export function estimateHomeTrackWidth(viewportWidth: number): number {
  const narrow = viewportWidth < 768;
  const protoScale = narrow
    ? Math.min(1.05, Math.max(0.85, viewportWidth / 900))
    : 1;
  const maxBd = 1280 * protoScale;
  const shell = Math.max(280, Math.min(viewportWidth, maxBd));
  const pad = appStoreSpacing(shell);
  return Math.max(200, shell - 2 * pad);
}

export function homeGridLayoutEqual(a: HomeGridLayout, b: HomeGridLayout): boolean {
  return (
    a.cols === b.cols &&
    Math.abs(a.slotGi - b.slotGi) < 1 &&
    Math.abs(a.gap - b.gap) < 1 &&
    Math.abs(a.padX - b.padX) < 1
  );
}

/** Minimum uniform spacing (edge + gap). */
const SPACING_MIN = 10;

/**
 * App Store / iOS home-screen spacing — ~4% of width on phone, slightly tighter on wide screens.
 */
export function appStoreSpacing(trackWidth: number): number {
  if (trackWidth < 768) {
    return Math.round(Math.min(18, Math.max(12, trackWidth * 0.04)));
  }
  if (trackWidth < 1024) {
    return Math.round(Math.min(20, Math.max(14, 10 + trackWidth * 0.01)));
  }
  return Math.round(Math.min(24, Math.max(16, 12 + trackWidth * 0.008)));
}

/**
 * Today's bands for phones and desktops (unchanged, so both render exactly as before at scale 1).
 * Phone 3–4, tablet-width 4–6, desktop 5–8, wide 6–10 — pack tiles to fill the row.
 */
function denseBandConfig(contentWidth: number): HomeGridBreakpointConfig {
  if (contentWidth < 420) {
    return {
      minCols: 3,
      maxCols: 4,
      minGi: 64,
      maxGi: Math.min(108, 56 + contentWidth * 0.14),
    };
  }
  if (contentWidth < 640) {
    return {
      minCols: 3,
      maxCols: 4,
      minGi: 68,
      maxGi: Math.min(112, 60 + contentWidth * 0.1),
    };
  }
  if (contentWidth < 900) {
    return {
      minCols: 4,
      maxCols: 6,
      minGi: 70,
      maxGi: Math.min(104, 64 + contentWidth * 0.045),
    };
  }
  if (contentWidth < 1100) {
    return {
      minCols: 5,
      maxCols: 8,
      minGi: 72,
      maxGi: Math.min(100, 68 + contentWidth * 0.028),
    };
  }
  return {
    minCols: 6,
    maxCols: 10,
    minGi: 74,
    maxGi: Math.min(104, 72 + contentWidth * 0.018),
  };
}

/** PageShell side gutter from 40rem up (`@bp-sm:px-6`), in design px. */
const SHELL_GUTTER_DESIGN_PX = 24;

/**
 * Track width at the compact/wide layout boundary (48rem minus both gutters). Below it a tablet
 * or kiosk is on the compact layout (iPad portrait ≈ 665, portrait kiosk 672).
 */
export const HOME_GRID_WIDE_TRACK_FROM = COMPACT_BELOW_DESIGN_PX - 2 * SHELL_GUTTER_DESIGN_PX;

/** Tablet and kiosk, compact layout: fewer, bigger cards (spec §3.4). */
const TOUCH_COMPACT_BAND: HomeGridBreakpointConfig = { minCols: 4, maxCols: 5, minGi: 104, maxGi: 132 };
/** Tablet and kiosk, wide layout (iPad landscape, landscape kiosk). */
const TOUCH_WIDE_BAND: HomeGridBreakpointConfig = { minCols: 5, maxCols: 8, minGi: 104, maxGi: 128 };

/**
 * Column + icon bounds for the browse track width (design px) and device class:
 * - phone and desktop: today's bands (as many columns as fit, icons up to ~104–112);
 * - tablet and kiosk: a comfortable card size — compact 4–5 columns of 104–132, wide 5–8 of 104–128.
 */
export function getHomeGridBreakpointConfig(
  contentWidth: number,
  device: DeviceClass,
  orientation?: Orientation
): HomeGridBreakpointConfig {
  const config =
    device === 'tablet' || device === 'kiosk'
      ? { ...(contentWidth < HOME_GRID_WIDE_TRACK_FROM ? TOUCH_COMPACT_BAND : TOUCH_WIDE_BAND) }
      : denseBandConfig(contentWidth);
  if (orientation !== 'portrait') return config;
  const maxCols = Math.min(config.maxCols, PORTRAIT_MAX_COLS);
  return { ...config, maxCols, minCols: Math.min(config.minCols, maxCols) };
}

/** Portrait screens show at most this many game columns (owner: 6 or 7). */
export const PORTRAIT_MAX_COLS = 7;

/** Grid track width: cols×slot + (cols−1)×gap must fit contentWidth. */
export function homeGridFits(
  contentWidth: number,
  cols: number,
  gap: number,
  slotGi: number,
): boolean {
  return cols * slotGi + Math.max(0, cols - 1) * gap <= contentWidth + 0.5;
}

/**
 * Layout from **content-box** width (inside horizontal padding):
 *   contentWidth ≥ cols×slot + (cols−1)×gap
 *
 * Prefers the most columns that keep icons in [minGi, maxGi].
 * When icons hit maxGi, gap expands so the row still fills (1fr CSS cells).
 * padX mirrors gap for springboard edge rhythm.
 */
export function computeHomeGridLayout(
  contentWidth: number,
  config: HomeGridBreakpointConfig,
  edgePadHint?: number,
): HomeGridLayout {
  const { minCols, maxCols, minGi, maxGi } = config;
  const targetSpacing = edgePadHint ?? appStoreSpacing(contentWidth + 2 * SPACING_MIN);

  if (contentWidth <= 0 || maxCols < minCols) {
    return { cols: minCols, slotGi: minGi, gap: targetSpacing, padX: targetSpacing };
  }

  for (let cols = maxCols; cols >= minCols; cols--) {
    const gaps = Math.max(0, cols - 1);
    const rawSlot = (contentWidth - gaps * targetSpacing) / cols;

    if (rawSlot >= minGi && rawSlot <= maxGi) {
      return {
        cols,
        slotGi: Math.round(rawSlot),
        gap: targetSpacing,
        padX: targetSpacing,
      };
    }

    if (rawSlot > maxGi) {
      const slotGi = maxGi;
      const gap =
        gaps > 0
          ? Math.max(SPACING_MIN, (contentWidth - cols * slotGi) / gaps)
          : targetSpacing;
      if (slotGi >= minGi) {
        // Keep padX === targetSpacing (edgePadHint) so shell measure stays stable.
        return { cols, slotGi: Math.round(slotGi), gap: Math.round(gap), padX: targetSpacing };
      }
      continue;
    }
  }

  const cols = minCols;
  const gaps = Math.max(0, cols - 1);
  const gap = Math.max(
    SPACING_MIN,
    gaps > 0 ? (contentWidth - cols * minGi) / gaps : targetSpacing,
  );
  const slotGi = Math.min(
    maxGi,
    Math.max(minGi, (contentWidth - gaps * gap) / cols),
  );

  return {
    cols,
    slotGi: Math.round(slotGi),
    gap: Math.round(gap),
    padX: targetSpacing,
  };
}

/** @deprecated use computeHomeGridLayout with getHomeGridBreakpointConfig */
export function optimalHomeGridCols(
  contentWidth: number,
  _gap: number,
  minGi: number,
  maxGi: number,
  minCols: number,
  maxCols: number,
): number {
  return computeHomeGridLayout(contentWidth, { minCols, maxCols, minGi, maxGi }).cols;
}

/** SSR / first paint — match post-measure layout to avoid reload jitter. Design px in and out. */
export function fallbackHomeGridLayout(
  viewportWidth: number,
  device: DeviceClass,
  orientation?: Orientation
): HomeGridLayout {
  const contentWidth = estimateHomeTrackWidth(viewportWidth);
  const config = getHomeGridBreakpointConfig(contentWidth, device, orientation);
  return computeHomeGridLayout(contentWidth, config);
}

/** Read content-box width of a padded shell (excludes horizontal padding). */
export function measureHomeContentWidth(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const padL = parseFloat(style.paddingLeft) || 0;
  const padR = parseFloat(style.paddingRight) || 0;
  const borderBox = el.getBoundingClientRect().width;
  const content = borderBox - padL - padR;
  if (content > 0) return content;
  return Math.max(0, el.clientWidth - padL - padR);
}
